import { useAppStore } from '@/core/stores/useAppStore'
import { logger } from '@/core/utils/logger'
import { getErrorMessage } from '@/core/types/errors'

interface ErrorHandlerOptions {
  /** Show toast notification to user (default: true) */
  showNotification?: boolean
  /** Log error to console/logger (default: true) */
  logError?: boolean
  /** Re-throw error after handling (default: false) */
  rethrow?: boolean
  /** Custom notification message (overrides error message) */
  customMessage?: string
}

/**
 * Centralized error handler for consistent error handling across the app.
 *
 * @example
 * // Basic usage - shows notification + logs
 * handleError(error)
 *
 * // Silent logging only
 * handleError(error, { showNotification: false })
 *
 * // Custom message
 * handleError(error, { customMessage: 'Failed to save project' })
 *
 * // Rethrow for caller to handle
 * handleError(error, { rethrow: true })
 */
export function handleError(error: unknown, options: ErrorHandlerOptions = {}): string {
  const {
    showNotification = true,
    logError = true,
    rethrow = false,
    customMessage
  } = options

  const message = customMessage || getErrorMessage(error)

  if (logError) {
    logger.error(message, error)
  }

  if (showNotification) {
    try {
      const appStore = useAppStore()
      appStore.addNotification(message, 'error')
    } catch {
      // AppStore not available (e.g., during app initialization)
      logger.warn('AppStore not available for notification:', message)
    }
  }

  if (rethrow) {
    throw error
  }

  return message
}
